import { useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Select, Switch, Space, App } from 'antd';
import { createAiModel, updateAiModel, type AiModel } from '@/api/aiModels';

const PROVIDERS = ['anthropic', 'openai', 'deepseek', 'qwen'];

interface Props {
  open: boolean;
  editing: AiModel | null;
  providerLocked?: string;
  onClose: () => void;
  onSaved: () => void;
}

export function ModelFormModal({ open, editing, providerLocked, onClose, onSaved }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm();

  // Only touch the form while open, so setFieldsValue never runs on an
  // unconnected instance (avoids the "useForm not connected" warning).
  useEffect(() => {
    if (!open) return;
    if (editing) form.setFieldsValue(editing);
    else {
      form.resetFields();
      form.setFieldsValue({
        provider: providerLocked ?? 'anthropic',
        enabled: true,
        supportsVision: false,
        acceptsPdfNatively: false,
      });
    }
  }, [open, editing, providerLocked, form]);

  async function submit() {
    const v = await form.validateFields();
    try {
      if (editing) await updateAiModel(editing.model, v);
      else await createAiModel(v);
      message.success('Saved');
      onSaved();
      onClose();
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  return (
    <Modal
      open={open}
      title={editing ? `Edit ${editing.model}` : `Add model${providerLocked ? ` · ${providerLocked}` : ''}`}
      onOk={submit}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item name="model" label="Model id" rules={[{ required: true }]}>
          <Input disabled={!!editing} placeholder="e.g. claude-opus-4" />
        </Form.Item>
        <Form.Item name="provider" label="Provider" rules={[{ required: true }]}>
          <Select disabled={!!providerLocked} options={PROVIDERS.map((p) => ({ value: p, label: p }))} />
        </Form.Item>
        <Form.Item name="apiModelId" label="API model id (optional, defaults to model id)">
          <Input placeholder="string sent to the provider API" />
        </Form.Item>
        <Space size="large">
          <Form.Item name="inputPrice" label="Input $/1M" rules={[{ required: true }]}>
            <InputNumber min={0} step={0.01} />
          </Form.Item>
          <Form.Item name="outputPrice" label="Output $/1M" rules={[{ required: true }]}>
            <InputNumber min={0} step={0.01} />
          </Form.Item>
        </Space>
        <Space size="large">
          <Form.Item name="supportsVision" label="Vision" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item name="acceptsPdfNatively" label="PDF native" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item name="enabled" label="Enabled" valuePropName="checked"><Switch /></Form.Item>
        </Space>
      </Form>
    </Modal>
  );
}
